import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Drop files onto an open conversation to attach them (Teampace 2858, 1.13.0): while files are
 * dragged over the conversation (or the thread beside it), it shows "Drop to attach", and a drop
 * hands them to the composer. Only file drags: dragging text or a link inside Stitch is left alone.
 * The browser, the Mac app and the Windows app all hand the page ordinary file drops.
 */
export function useFileDrop(composer: RefObject<HTMLElement | null>, opts: { on: boolean; pane: string; onFiles: (files: File[]) => void }): boolean {
  const [over, setOver] = useState(false);
  const onFiles = useRef(opts.onFiles);
  onFiles.current = opts.onFiles;
  useEffect(() => {
    if (!opts.on) return;
    const pane = composer.current?.closest<HTMLElement>(opts.pane);
    if (!pane) return;
    const files = (e: DragEvent) => Boolean(e.dataTransfer && [...e.dataTransfer.types].includes('Files'));
    // Entering and leaving children fire too: count them, so the overlay doesn't flicker.
    let depth = 0;
    const enter = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      depth += 1;
      setOver(true);
    };
    const move = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!files(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setOver(false);
    };
    const drop = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      depth = 0;
      setOver(false);
      const list = e.dataTransfer ? [...e.dataTransfer.files] : [];
      if (list.length) onFiles.current(list);
    };
    // A drag that ends anywhere else (Esc, dropped outside the window) clears the overlay. A file
    // dropped beside the conversation does nothing (the browser would open it in place of Stitch).
    const reset = (e: Event) => {
      depth = 0;
      setOver(false);
      if (e instanceof DragEvent && e.type === 'drop' && files(e)) e.preventDefault();
    };
    // (Any conversation or thread that takes drops: with a thread open, both do.)
    const elsewhere = (e: DragEvent) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!files(e) || t?.closest('[data-file-drop]')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    pane.dataset.fileDrop = '';
    pane.addEventListener('dragenter', enter);
    pane.addEventListener('dragover', move);
    pane.addEventListener('dragleave', leave);
    pane.addEventListener('drop', drop);
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    window.addEventListener('dragover', elsewhere);
    return () => {
      window.removeEventListener('dragover', elsewhere);
      delete pane.dataset.fileDrop;
      pane.removeEventListener('dragenter', enter);
      pane.removeEventListener('dragover', move);
      pane.removeEventListener('dragleave', leave);
      pane.removeEventListener('drop', drop);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, [composer, opts.on, opts.pane]);
  return over;
}

/** Files on the clipboard (a screenshot, files copied in Finder or Explorer), for pasting. */
export function pastedFiles(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of [...data.items]) {
    if (item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (!f) continue;
    // A pasted screenshot has no name of its own.
    out.push(f.name && f.name !== 'image.png' ? f : new File([f], `Pasted ${new Date().toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '.')}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type }));
  }
  return out;
}
